import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {
  emptyDadataResult,
  emptyFias, emptyImportSalePoint, emptyImportSalePointClient,
  emptySchema,
  emptySchemaLine,
  emptySiteJournal,
  emptySiteJournalLine,
  emptySiteJournalLineProfile,
  emptySiteProfile, emptySitesStatistics,
  IDadataResult,
  IFias, IImportSalePoint, IImportSalePointClient,
  ISchema,
  ISchemaLine,
  ISiteJournal,
  ISiteJournalLine,
  ISiteJournalLineProfile,
  ISiteProfile, ISitesStatistics
} from "@retail-data-factory/rdf-angular-types-api";
import {emptyServiceResponse, IServiceResponse} from "../../../../services/external-api/response-interfaces";
import {mockSchemaList} from "./mock-schema";
import {emptySite, ISite} from "@retail-data-factory/rdf-angular-types-api";
import {DataService} from "../../../../services/external-api/data.service";
import {map} from "rxjs/operators";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";
import {Presenter} from "../../../internal/presenter/presenter";
import * as moment from "moment";
import {
  emptySiteJournalStatLine, emptySiteJournalStatTotal,
  ISiteJournalStatLine, ISiteJournalStatTotal
} from "@retail-data-factory/rdf-angular-types-api";
import {
  emptyDadataSuggestion,
  IDadataSuggestion
} from "@retail-data-factory/rdf-angular-types-api";
import {TSiteJournalStatus} from "@retail-data-factory/rdf-angular-types-api";
import {Moment} from "moment";
import {HttpClient} from "@angular/common/http";


@Injectable({
  providedIn: 'root'
})
export class ApiSitesService {
  private siteArray: Array<ISite> = [];

  constructor(
    private presenter: Presenter,
    private dataService: DataService,
    private configurationManager: ConfigurationManager,
    private http: HttpClient
  ) {
  }

  dateFrom: Moment;
  dateTo: Moment;

  /**
   * Получить список сайтов
   * @param partyId - пати в которой нужно получить сайты - если не передано - то в своей
   */
  getSites(partyId?: string, fromMemory = false, limit: number = 100, offset: number = 0, search = ''): Observable<Array<ISite>> {
    const param = partyId ? {partyid: partyId} : {};
    if (fromMemory === true && this.siteArray.length > 0) {
      const tmp = JSON.parse(JSON.stringify(this.siteArray));
      return of(tmp);
    }
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/sites?limit=${limit}&offset=${offset}&search=${search}`, param).pipe(
      map((result: IServiceResponse) => {
          this.siteArray = result.report.map(t => this.mapSiteFromService(t))
          return JSON.parse(JSON.stringify(this.siteArray));
        }
      )
    );
  }

  /**
   * Получить сайт по ID
   * @param siteId
   */
  getSite(siteId: string): Observable<ISite> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/sites/${siteId}`, {}).pipe(
      map((result: IServiceResponse) => {
          if (result.report.length > 0) {
            return this.mapSiteFromService(result.report[0]);
          } else {
            return {...emptySite};
          }

        }
      )
    );

  }


  createSite(site: ISite): Observable<IServiceResponse> {
    const body = this.mapsSiteToService(site);
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/site`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  updateSite(site: ISite): Observable<IServiceResponse> {
    const body = this.mapsSiteToService(site);
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/site/${site.id}`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  saveSite(site: ISite): Observable<IServiceResponse> {
    return site.id ?
      this.updateSite(site) :
      this.createSite(site);
  }

  getSchemaList(partyId?: string): Observable<Array<ISchemaLine>> {
    if (!partyId) {
      return of([...mockSchemaList.filter(t => t.current.partyId === this.presenter.currentPartyId)]);
    } else {
      return of([...mockSchemaList.filter(t => t.current.partyId === partyId)]);
    }
  }

  getSchema(id: string): Observable<ISchema> {
    let schema: ISchema = {...emptySchema};

    mockSchemaList.forEach(curr => {
      if (curr.current.id === id) {
        schema = curr.current;
      }
      curr.versions.forEach(ver => {
        if (ver.id === id) {
          schema = ver;
        }
      });
    });
    return of({...schema});
  }

  createSchema(schema: ISchema): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }

  updateSchema(schema: ISchema): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }

  deleteSchema(id: string): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }

  /** Проверка адресов в DaData */
  checkAddressInDadata(addressArray: Array<string>): Observable<Array<IDadataResult>> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/dadata`, {}, addressArray).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => {
            const result: IDadataResult = {
              ...JSON.parse(JSON.stringify(emptyDadataResult)),
              ...t
            };
            return result;
          });
        }
      )
    );
  }

  /** Получение подсказок в DaData */
  getSuggestionsInDadata(query: String): Observable<Array<IDadataSuggestion>> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/dadata/suggestions`, {}, {query: query}).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => {
            const result: IDadataSuggestion = {
              ...JSON.parse(JSON.stringify(emptyDadataSuggestion)),
              ...t
            };
            return result;
          });
        }
      )
    );
  }

  /**
   * Получить список журналов
   * @param dateFrom дата с в формате YYYYMMDD
   * @param dateUntil дата по в формате YYYYMMDD
   */
  getSiteJournals(dateFrom: string, dateUntil: string): Observable<Array<ISiteJournal>> {
    const param = {
      date_from: dateFrom,
      date_until: dateUntil
    }
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapSiteJournalFromService(t));
        }
      )
    );
  }

  /**
   * Получить журнал
   * @param journalId
   */
  getSiteJournal(journalId): Observable<ISiteJournal> {

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}`, {}).pipe(
      map((result: IServiceResponse) => {
          if (result?.report && Array.isArray(result.report) && result.report.length > 0) {
            return this.mapSiteJournalFromService(result.report[0]);
          } else {
            return JSON.parse(JSON.stringify(emptySiteJournal))
          }
        }
      )
    );
  }


  createSiteJournal(journal: ISiteJournal): Observable<IServiceResponse> {
    const body = {
      name: journal.name,
      description: journal.description,
      type: journal.typeCode,
      profile: this.mapJournalToService(journal)
    }
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals`, {}, body);

  }

  updateSiteJournal(journal: ISiteJournal): Observable<IServiceResponse> {
    const body = {
      name: journal.name,
      description: journal.description,
      profile: this.mapJournalToService(journal)
    }
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journal.id}`, {}, body);

  }

  saveSiteJournal(journal: ISiteJournal): Observable<IServiceResponse> {
    return journal.id ? this.updateSiteJournal(journal) : this.createSiteJournal(journal)
  }

  createSitesForJournal(journalId: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/sites`, {}, {});
  }

  /**
   * Смена статуса журнала
   * @param journalId
   * @param status - новый статус журнала - возможные варианты  'pending' | 'process' | 'complete' | 'error'
   */
  setJournalStatus(journalId: string, status: 'pending' | 'process' | 'complete' | 'error' | 'active' | 'cancel' | 'checked'): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}?action=${status}`, {}, {});
  }


  getSiteJournalLines(journalId: string): Observable<Array<ISiteJournalLine>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/lines`, {}).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapSiteJournalLineFromService(journalId, t));
        }
      )
    );
  }

  getSiteJournalLine(journalId: string, lineId): Observable<ISiteJournalLine> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/lines/${lineId}`, {}).pipe(
      map((result: IServiceResponse) => {
          return result.report.length > 0 ? this.mapSiteJournalLineFromService(journalId, result.report[0]) : JSON.parse(JSON.stringify(emptySiteJournalLine));
        }
      )
    );
  }

  saveSiteJournalLine(line: ISiteJournalLine): Observable<IServiceResponse> {
    return line.id ? this.updateSiteJournalLine(line) : this.createSiteJournalLine(line)
  }

  createSiteJournalLine(line: ISiteJournalLine): Observable<IServiceResponse> {
    line.profile = {
      ...JSON.parse(JSON.stringify(emptySiteJournalLineProfile)),
      ...line.profile
    }
    line.profile.source.id = line.sourceCode;
    line.profile.source.name = line.sourceName;
    line.profile.source.address = line.sourceAddress;

    const body = {
      profile: line.profile
    }
    if (line.siteId) {
      body['site_id'] = line.siteId
    }
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${line.journalId}/lines`, {}, body);

  }

  updateSiteJournalLine(line: ISiteJournalLine): Observable<IServiceResponse> {


    const body = {
      profile: this.mapSiteJournalLineToService(line)
    }
    if (line.siteId) {
      body['site_id'] = line.siteId
    }
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${line.journalId}/lines/${line.id}`, {}, body);
  }


  clearSiteJournalLines(journalId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/lines`, {});
  }

  deleteSiteJournalLines(line: ISiteJournalLine): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${line.journalId}/lines/${line.id}`, {});
  }


  getImgSite(id: string, partyId: string) {
    const param = {partyid: partyId};
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/siteimage/${id}`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report;
        }
      )
    );
  }

  uploadImg(id: string, img): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/siteimage/${id}`, {}, img).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    )
  }


  getImportSalesPoints(props: {
                         sourceSystemCode: string,
                         count: number,
                         onlyNotParsed?: boolean
                       }
  ): Observable<Array<IImportSalePoint>> {
    const param = {
      source_system_code: props.sourceSystemCode,
      count: props.count,
      only_not_parsed: props.onlyNotParsed ? props.onlyNotParsed : false
    };
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/imports/salespoints`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapSalesPointFromService(t));
        }
      )
    );
  }

  getImportSalesPointsClients(): Observable<Array<IImportSalePointClient>> {

    const param = {};

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/imports/salespoints/clients`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapSalesPointClientsFromService(t));
        }
      )
    );
  }

  /**
   * Изменить статус линии журнала сайтов
   * @param journalId
   * @param lineId
   * @param action
   */
  updateJournalSiteStatus(journalId: string, lineId: string, action: 'ok' | 'error' | 'manual' | 'not_parsed'): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/lines/${lineId}?action=${action}`, {}, {});
  }


  getJournalStatisticsAll(journalId: string): Observable<Array<ISiteJournalStatLine>> {
    const param = {};

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/statistics/allsites`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapJournalStatFromService(t));
        }
      )
    );
  }

  getJournalStatisticsMatched(journalId: string): Observable<Array<ISiteJournalStatLine>> {
    const param = {};

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/statistics/matchedsites`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapJournalStatFromService(t));
        }
      )
    );
  }

  getJournalStatisticsUnmatched(journalId: string): Observable<Array<ISiteJournalStatLine>> {
    const param = {};

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/statistics/unmatchedsites`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => this.mapJournalStatFromService(t));
        }
      )
    );
  }


  getJournalStatisticsTotal(journalId: string): Observable<Array<ISiteJournalStatTotal>> {
    const param = {};

    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/journals/${journalId}/statistics/total`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(t => ({
            ...JSON.parse(JSON.stringify(emptySiteJournalStatTotal)),
            countAll: t['cnt_all'],
            countExists: t['cnt_exists'],
            countNew: t['cnt_new']
          }));
        }
      )
    );
  }


  getJournalsForProject(projectId: string): Observable<Array<ISiteJournal>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/projects/${projectId}/journals`, {}).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(item => ({
            ...JSON.parse(JSON.stringify(emptySiteJournal)),
            id: item['journal_id'],
            name: item['journal_name'],
          }));
        }
      )
    );
  }

  addJournalToProject(projectId: string, journalId: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/projects/${projectId}/journals/${journalId}`, {}, {});

  }

  deleteJournalFromProject(projectId: string, journalId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/projects/${projectId}/journals/${journalId}`, {});

  }

  deleteAllJournalsFromProject(projectId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/projects/${projectId}/journals`, {});

  }

  getSitesForProject(projectId: string): Observable<Array<ISite>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/projects/${projectId}/sites`, {}).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(item => this.mapSiteFromService(item));
        }
      )
    );
  }

  getSitesStatistics(): Observable<ISitesStatistics> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/statistics`, {}).pipe(
      map((result: IServiceResponse) => {
          return result.report.length > 0 ? this.mapSiteStatisticsFromService(result.report[0]) : JSON.parse(JSON.stringify(emptySitesStatistics));

        }
      )
    );
  }

  getNearestSites(lat: number, lon: number, distance = 5, brand = ''): Observable<Array<ISite>> {
    const param = {
      lat: lat,
      lon: lon,
      distance: distance
    }
    if (brand) {
      param['brand'] = encodeURIComponent(brand);
    }
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiSitesUrl')}/sites/nearest`, param).pipe(
      map((result: IServiceResponse) => {
          return result.report.map(item => this.mapSiteFromService(item));
        }
      )
    );
  }

  getNearestSitesYandex(lat: number, lon: number, brand: string = '') {
    const url = 'https://search-maps.yandex.ru/v1/'
    const param = {
      apikey: 'fbd862a8-a5f4-4fc9-a945-41b55267c755',
      lang: 'ru_RU',
      text: brand,
      ll: lon + ', ' + lat,
      spn: '0.4, 0.4',
      rspn: 1,
      results: 5
    }
    return this.http.get(url, {params: param}).pipe(map((result) => {
        return result['features'].map(item =>{
          const result = {
            item: item,
            address: item['properties']['CompanyMetaData']['address'],
            lat: item['geometry']['coordinates'][1],
            lon: item['geometry']['coordinates'][0],
            brand: item['properties']['name']
          }
          return result
        });
      })
    );
  }


  mapSiteStatisticsFromService(item: object): ISitesStatistics {
    const result: ISitesStatistics = {
      ...JSON.parse(JSON.stringify(emptySitesStatistics)),
      countSites: item['count_sites'] ? item['count_sites'] : 0
    };
    return result;
  }

  mapJournalStatFromService(item: object): ISiteJournalStatLine {
    const result: ISiteJournalStatLine = {
      ...JSON.parse(JSON.stringify(emptySiteJournalStatLine)),
      sourceSystemCode: item['source_system_code'],
      sourceSystemId: item['id_ste'], //item['source_system_id']!==null && item['source_system_id']!==undefined?item['source_system_id']: item['id_action'],

      region: item['region'],
      city: item['city'],
      address: item['address'],
      brand: item['brand'],
      visits: item['visits'],
      cashDeskCount: item['cash_desk_count'],
      directorName: item['director_name'],
      directorPhone: item['director_phone'],
      lprCategory: item['lpr_category'],
      lprName: item['lpr_name'],
      lprPhone: item['lpr_phone'],
      statusCode: item['status'],
      presentInMdm: item['is_in_mdm'],

      coordinates: item['coordinates'],
      nearestCity: item['nearest_city'],
      distance_km: item['distance_km'],
    };

    return result;
  }

  mapSalesPointFromService(item: object): IImportSalePoint {
    const result: IImportSalePoint = {
      ...JSON.parse(JSON.stringify(emptyImportSalePoint)),
      sourceSystemCode: item['source_system_code'],
      id: item['id'].toString(),
      isActive: item['is_active'],
      spid: item['spid'],
      name: item['name'],
      legalName: item['legal_name'],
      address: item['address'],
      lat: item['lat'],
      lon: item['lon'],
      description: item['description'],
      cityId: item['city_id'],
      tradeNetworkId: item['trade_network_id'],
      location: item['location'],
      pointTypeId: item['point_type_id'],
      fiasCode: item['fias_code'],
      partyId: item['party_id'],
    };
    return result;


  }

  mapSalesPointClientsFromService(item: object): IImportSalePointClient {

    const result: IImportSalePointClient = {
      ...JSON.parse(JSON.stringify(emptyImportSalePointClient)),
      name: item['source_system_code'],
      stageSites: item['count_sites_all'],
      mdmSites: item['count_sites_mdm']
    };

    return result;
  }


  mapSiteJournalFromService(item: object): ISiteJournal {
    const result: ISiteJournal = {
      ...JSON.parse(JSON.stringify(emptySiteJournal)),
      id: item['id'].toString(),
      partyId: item['party_id'].toString(),
      name: item['name'],
      description: item['description'],
      dateCreate: moment(item['create_date'], 'YYYYMMDD HH:mm:ss').toDate(),
      idUserCreate: item['user_create_id'].toString(),
      userCreateName: item['user_create_name'],
      statusCode: item['status_code'],
      typeCode: item['type_code'],
      lines: [],
      sourceData: {
        availableSites: [],
        selectedSites: []
      },
      sourceDataType: '',
      profile: item['profile'] ? item['profile'] : {},
    }
    result.sourceData.availableSites = result.profile && Array.isArray(result.profile['available_sites']) ? JSON.parse(JSON.stringify(result.profile['available_sites'])) : [];
    result.sourceData.selectedSites = result.profile && Array.isArray(result.profile['selected_sites']) ? JSON.parse(JSON.stringify(result.profile['selected_sites'])) : [];
    return result;
  }

  mapJournalToService(journal: ISiteJournal): object {

    const result = {
      ...journal.profile,
      available_sites: journal.sourceData.availableSites,
      selected_sites: journal.sourceData.selectedSites,
    }
    return result
  }

  mapSiteJournalLineFromService(journalId: string, item: object): ISiteJournalLine {
    const dadata: IDadataResult = {
      ...JSON.parse(JSON.stringify(emptyDadataResult)),
      ...(<ISiteJournalLineProfile>item['profile'])?.dadata,
      region_with_type: item['dadata_region'],
      city: item['dadata_city'],
      result: item['dadata_address'],
      qc: item['dadata_quality_code'],
      qc_complete: item['dadata_mail_quality_code'],
      qc_house: item['dadata_house_quality_code'],
      qc_geo: item['dadata_geo_quality_code'],

    }
    const siteProfile: ISiteProfile = {
      ...JSON.parse(JSON.stringify(emptySiteProfile)),
      Code: item['site_code'],
      Name: item['site_name'],
      Address: item['site_address'],
      source_name: item['source_name'],
      dadata: JSON.parse(JSON.stringify(dadata))
    }
    const site: ISite = {
      ...JSON.parse(JSON.stringify(emptySite)),
      code: item['site_code'],
      name: item['site_name'],
      id: item['site_id']?.toString(),
      profile: siteProfile
    };

    const result: ISiteJournalLine = {
      ...JSON.parse(JSON.stringify(emptySiteJournalLine)),
      id: item['id'].toString(),
      journalId: journalId,
      partyId: item['party_id'].toString(),
      statusCode: item['status_code'],
      siteId: item['site_id'],
      site: site,
      dateCreate: moment(item['create_date'], 'YYYY-MM-DD HH:mm:ss'),
      idUserCreate: item['user_create_id'].toString(),

      sourceCode: (<ISiteJournalLineProfile>item['profile'])?.source?.id ?
        (<ISiteJournalLineProfile>item['profile'])?.source?.id :
        item['source_id']?.toString(),
      sourceName: (<ISiteJournalLineProfile>item['profile'])?.source?.name ?
        (<ISiteJournalLineProfile>item['profile'])?.source?.name :
        item['source_name'],

      sourceAddress: (<ISiteJournalLineProfile>item['profile'])?.source?.address ?
        (<ISiteJournalLineProfile>item['profile'])?.source?.address :
        item['source_address'],

      dadataResult: JSON.parse(JSON.stringify(dadata)),
      profile: item['profile']
    }
    return result;
  }

  mapSiteJournalLineToService(line: ISiteJournalLine): ISiteJournalLineProfile {
    const result: ISiteJournalLineProfile = {
      ...JSON.parse(JSON.stringify(emptySiteJournalLineProfile)),
      ...line.profile,
      source: {
        ...line.profile.source,
        id: line.sourceCode,
        name: line.sourceName,
        address: line.sourceAddress,
      }
    }

    return result;
  }

  mapSiteFromService(item: object): ISite {
    const profile: ISiteProfile = {
      ...JSON.parse(JSON.stringify(emptySiteProfile)),
      ...item['profile']
    }

    const result: ISite = {
      ...JSON.parse(JSON.stringify(emptySite)),

      id: item['ID'].toString(),
      partyId: item['partyId'].toString(),
      statusCode: item['Status'] ? item['Status'] : profile.status,

      code: item['Code'] ? item['Code'] : profile.Code,
      name: item['Name'] ? item['Name'] : profile.Name,
      address: item['Address'] ? item['Address'] : profile.Address,

      dateUpdate: item['update_date'] ? moment(item['update_date'], 'YYYYMMDD HH:mm:ss').toDate() : null,

      // phone: profile.main_contact.phone,
      // email: profile.main_contact.email,

      schema: profile.schema,
      data: profile.data,
      location: {
        ...JSON.parse(JSON.stringify(emptySite.location)),
        ...JSON.parse(JSON.stringify(profile.location))
      },
      fias: {
        ...JSON.parse(JSON.stringify(emptySite.fias)),
        // ...JSON.parse((JSON.stringify(profile.fias)))
      },
      // addressConfirmed: profile.address_confirmation,
      profile: profile
    };
    return result;
  }

  // mapSiteFromServiceOld(item: object): ISite {
  //   const site: ISite = {
  //     ...emptySite,
  //     id: item['ID'], // arr[0]["id"],
  //     partyId: item['partyId'],
  //
  //
  //     code: item['Code'] ? item['Code'] : item['profile']['Code'],
  //     name: item['Name'] ? item['Name'] : item['profile']['Name'],
  //     //openDate: item['Profile']['OpenDate'],
  //
  //     address: item['Address'] ? item['Address'] : item['profile']['Address'],
  //     phone: item['profile']['Phone'],
  //     fax: item['profile']['Fax'],
  //     email: item['profile']['Email'],
  //     typeSiteCode: item['type'] ? item['type'] : item['profile']['TypeSite'],
  //     geoPosition: item['profile']['Geo Position'],
  //     lat: item['profile']['LAT'],
  //     lng: item['profile']['LNG'],
  //     statusCode: item['Status'] ? item['Status'] : item['profile']['status'],
  //     paymentTypeCode: item['profile']['Payment Type'],
  //     img: '',
  //     // информация о дирекции
  //     director: {
  //       name: item['profile']['Director Name'],
  //       phone: item['profile']['Director Phone'],
  //       email: item['profile']['Director Email'],
  //     },
  //     assistantDirector: {
  //       name: item['profile']['Assistant Director Name'],
  //       phone: item['profile']['Assistant Director Phone'],
  //       email: item['profile']['Assistant Director Email'],
  //     },
  //     hrHead: {
  //       name: item['profile']['Head HR Name'],
  //       phone: item['profile']['Head HR Phone'],
  //       email: item['profile']['Head HR Email'],
  //     },
  //     regionalDirector: {
  //       name: item['profile']['Regional Director Name'],
  //       phone: item['profile']['Regional Director Phone'],
  //       email: item['profile']['Regional Director Email'],
  //     },
  //     territoryDirector: {
  //       name: item['profile']['Territory Director Name'],
  //       phone: item['profile']['Territory Director Phone'],
  //       email: item['profile']['Territory Director Email'],
  //     },
  //     //дополнительные атрибуты
  //     publicTransport: item['profile']['Public Transport'],
  //     parkingSize: item['profile']['Parking Size'],
  //     workingTime: item['profile']['Open Hours Working Days'],
  //
  //     schema: item['profile']['schema'],
  //     data: item['profile']['data'] ? item['profile']['data'] : {}
  //   };
  //   return site;
  // }
  mapsSiteToService(site: ISite): ISiteProfile {
    const result: ISiteProfile = {
      ...JSON.parse(JSON.stringify(emptySiteProfile)),
      ...JSON.parse(JSON.stringify(site.profile)),

      Code: site.code,
      Name: site.name,
      Address: site.address,

      // main_contact: {
      //   ...JSON.parse(JSON.stringify(emptySiteProfile.main_contact)),
      //   phone: site.phone,
      //   email: site.email
      // },

      location: site.location,
      fias: site.fias,
      schema: site.schema,
      data: site.data

    }
    return result;
  }

  decodeDadataQuality(qc: string) {
    switch (qc.toString()) {
      case '0':
        return 'Адрес распознан уверенно';
      case '1':
        return 'Остались «лишние» части';
      case '2':
        return 'Адрес пустой или заведомо «мусорный»';
      case '3':
        return 'Есть альтернативные варианты';
      default:
        return 'Распознавание не проводилось';
    }
  }

  decodeDadataQualityColor(qc: string) {
    switch (qc.toString()) {
      case '0':
        return '#569c71';
      case '1':
        return '#c7a642';
      case '2':
        return '#ec2024';
      case '3':
        return '#c7a642';
      default:
        return '#ec2024';
    }
  }

  decodeJournalLineStatusColor(statusCode: string) {
    switch (statusCode) {
      case 'PARSED_OK':
        return '#569c71';
      case 'PARSED_MANUAL':
        return '#c7a642';
      case 'PARSED_ERROR':
        return '#ec2024';
      default:
        return 'gray';
    }
  }

  /** Декодирование статуса адреса */
  decodeSearchSiteAddressStatus(line: ISiteJournalLine): string {
    if (line?.dadataResult?.qc?.toString() == '0') return 'Адрес подтвержден';
    //Иногда дадата выкидывает с ее точки зрения лишнее и подставляет в качестве адреса город, при этом возвращает 1, но по факту это мусорный адрес
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result?.length >= 20) return 'Сомнительный адрес';
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result?.length < 20) return 'Мусорный адрес';
    if (line?.dadataResult?.qc?.toString() === '2') return 'Мусорный адрес';
    if (line?.dadataResult?.qc?.toString() === '3') return 'Сомнительный адрес';
    return 'Другое'

  }

  /** Декодирование цвета статуса адреса */
  decodeSearchSiteAddressColor(line: ISiteJournalLine): string {
    if (line?.dadataResult?.qc?.toString() === '0') return '#569c71';
    //Иногда дадата выкидывает с ее точки зрения лишнее и подставляет в качестве адреса город, при этом возвращает 1, но по факту это мусорный адрес
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result?.length >= 20) return '#c7a642';
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result?.length < 20) return '#ec2024';
    if (line?.dadataResult?.qc?.toString() === '2') return '#ec2024';
    if (line?.dadataResult?.qc?.toString() === '3') return '#c7a642';
    return 'gray'
  }


  /** Текстовое описание статуса линии */
  decodeJournalLineStatus(line: ISiteJournalLine): string {
    switch (line?.statusCode) {
      case 'PARSED_OK': {
        return 'Адрес подтвержден';
      }
      case 'PARSED_MANUAL': {
        return 'Адрес не подтверждён';
      }
      default: {
        return 'Адрес не подтверждён'
      }
    }
  }

  /** Декодирование типа линии */
  decodeJournalLineType(line: ISiteJournalLine): number {
    if (line?.dadataResult?.qc?.toString() === '0') return 0;
    //Иногда дадата выкидывает с ее точки зрения лишнее и подставляет в качестве адреса город, при этом возвращает 1, но по факту это мусорный адрес
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result.length >= 20) return 1;
    if (line?.dadataResult?.qc?.toString() === '1' && line?.dadataResult?.result.length < 20) return 2;
    if (line?.dadataResult?.qc?.toString() === '2') return 2;
    if (line?.dadataResult?.qc?.toString() === '3') return 1;
    return 2
  }


  /** Цвет статуса линии */
  decodeJournalLineCircleColor(line: ISiteJournalLine): string {
    switch (line.statusCode) {
      case 'PARSED_OK':
        return '#569c71';
      case 'PARSED_MANUAL':
        return '#ec2024';
      case 'PARSED_ERROR':
        return '#ec2024';
      default:
        return 'gray';
    }
  }

  /** Текст в кружке для статуса линии */
  decodeJournalLineCircleText(line: ISiteJournalLine): string {
    switch (line.statusCode) {
      case 'PARSED_OK': {
        return '';
      }
      case 'PARSED_MANUAL': {
        return '!';
      }
      case 'PARSED_ERROR':
        return '!';
      default: {
        return '!'
      }
    }
  }

  decodeSiteJournalStatus(status: TSiteJournalStatus): string {
    switch (status) {
      case "ACTIVE":
        return 'Активный';
      case "CANCELED":
        return 'Отменен'
      case "CHECKED":
        return 'Проверен';
      case "PENDING":
        return 'Ожидает обработки'
      case "PROCESSING":
        return 'Обрабатывается'
      case 'COMPLETED':
        return 'Обработан';
      case 'DRAFT':
        return 'Новый';
      case 'ERROR':
        return 'Ошибка';
      default:
        return 'Не определен'
    }
  }

  decodeSiteJournalStatusColor(status: TSiteJournalStatus): string {
    switch (status) {
      case "ACTIVE":
        return '#569c71';
      case "CANCELED":
        return '#b6c0d4'
      case "CHECKED":
        return '#569c71';
      case "PENDING":
        return '#b6c0d4'
      case "PROCESSING":
        return '#b6c0d4'
      case 'COMPLETED':
        return '#569c71';
      case 'ERROR':
        return '#ec2024';
      case 'DRAFT':
        return '#3e5ff7';
      default:
        return '#b6c0d4'
    }
  }

  decodeStatusColorBorder(status: TSiteJournalStatus) {
    return `4px solid ${this.decodeSiteJournalStatusColor(status)}`
  }


}
